import { useEffect, useRef } from 'react';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import {
  fillText,
  monthLabel,
} from 'components/invoiceImport/invoiceImportText';
import Pagination from 'components/pagination/pagination';
import UseContext from 'hooks/useContext';
import InvoiceGroupRow from './InvoiceGroupRow';
import InvoiceRow, { CHECKBOX_CLASS_NAME, formatEuros } from './InvoiceRow';
import { groupedFooterText } from './invoiceGroupText';
import { PAGE_SIZE } from './useMonthInvoices';

const SKELETON_ROWS = 5;
// The five columns and the eye; the box column stays empty.
const SKELETON_COLUMNS = 6;

const HEADER_CELL_CLASS_NAME =
  'sticky top-0 z-30 py-3.5 text-left text-sm font-medium text-gray-700 bg-gray-50 border-b border-gray-100 whitespace-nowrap';

// A list shown with nothing to tick it with: no box is ever ticked.
const NO_SELECTION = {
  isSelected: () => false,
  toggle: () => {},
  pageState: 'none',
  togglePage: () => {},
  selectMany: () => {},
  unselectMany: () => {},
  seeInvoices: () => {},
};

// « Tout sélectionner » of the page: ticked when all of it is, half when some.
const PageCheckbox = ({ pageState, onToggle, label }) => {
  const box = useRef(null);
  useEffect(() => {
    box.current.indeterminate = pageState === 'some';
  }, [pageState]);
  return (
    <input
      ref={box}
      type="checkbox"
      className={CHECKBOX_CLASS_NAME}
      checked={pageState === 'all'}
      onChange={onToggle}
      aria-label={label}
    />
  );
};

// The look of the shared Thead, which only takes texts: this one starts with
// the box of the page. Without `pageCheckbox` (loading), that cell is empty.
const InvoiceTableHead = ({ headers, pageCheckbox }) => (
  <thead className="sticky top-0 z-10">
    <tr className="sticky top-0 z-30 border-b border-gray-900">
      <th className={`${HEADER_CELL_CLASS_NAME} pl-4 pr-2 w-px`}>
        {pageCheckbox}
      </th>
      {headers.map((header) => (
        <th key={header} className={`${HEADER_CELL_CLASS_NAME} px-2`}>
          {header.toUpperCase()}
        </th>
      ))}
      <th className={`${HEADER_CELL_CLASS_NAME} pl-2 pr-3 w-fit`} />
    </tr>
  </thead>
);

// The column headers stay still above the grey rows, which alone pulse.
const InvoiceListSkeleton = ({ headers }) => (
  <table className="table-auto w-full text-gray-500 ft-sm" aria-busy="true">
    <InvoiceTableHead headers={headers} />
    <tbody>
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <tr key={row}>
          <td className="py-7 pl-4 pr-2" />
          {Array.from({ length: SKELETON_COLUMNS }, (__, column) => (
            <td key={column} className="px-2 py-7">
              <div className="h-2 bg-gray-200 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

// The table and its footer, the same for the list and its loading. The tab
// scrolls, not the table: the head sticks to the top of the tab's scroll and
// the footer to its bottom, where flex-1 also keeps it under a short list.
const InvoiceTableFrame = ({ above, footer, children }) => (
  <div className="flex flex-col flex-1">
    {above}
    <div className="flex-1">{children}</div>
    <div className="sticky bottom-0 z-20 flex-shrink-0 flex flex-col gap-3 md:flex-row md:items-center border-t border-gray-100 bg-white px-8 py-4">
      {footer}
    </div>
  </div>
);

/**
 * The invoices of the month on "Paie & factures" (JBWEB-1269), one page as the
 * api gave it, with its loading, empty and failed states. `list`, `isLoading`,
 * `failed`, `page`, `setPage`, `reload` and `isFiltered` come from
 * `useMonthInvoices`; `period` ("2026-06") is the month shown, named by the
 * empty state.
 *
 * An empty month names the last month with invoices that the api found
 * (`list.lastPeriodWithInvoices`) and offers to show it with `onSelectMonth`
 * ("2026-05"). With filters (JBWEB-1270) an empty list does not mean an empty
 * month: it says no invoice matches, and offers `onClearFilters`.
 *
 * Each row has a box to tick, and the header one for the whole page
 * (JBWEB-1273): `selection` comes from `useInvoiceSelection`.
 *
 * Grouped by company or by group (`groupBy`, JBWEB-1309), the list is one row
 * per company or group, each unfolding into its invoices, read with
 * `listQuery`; the pages are pages of rows. The header has no box then: a row
 * ticks its own invoices.
 */
const InvoiceMonthList = ({
  list,
  isLoading,
  failed,
  page,
  setPage,
  reload,
  isFiltered,
  period,
  onSelectMonth,
  onClearFilters,
  selection = NO_SELECTION,
  groupBy,
  listQuery,
}) => {
  const { language } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceList.${key}`, fallback);

  const isSearching = Boolean(list?.discovery?.running);
  const headers = [
    text('columnNumber', 'N°'),
    text('columnCompany', 'Entreprise'),
    text('columnAmount', 'Montant TTC'),
    text('columnRemaining', 'Reste à payer'),
    text('columnStatus', 'Statut'),
  ];
  let content;

  if (failed) {
    // Never the empty state: a list that could not be read is not an empty one.
    content = (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-gray-700"
      >
        <div>
          {text('loadFailed', 'La liste des factures n’a pas pu être chargée.')}
        </div>
        <Button color="SECONDARY" onClick={reload}>
          {text('retry', 'Réessayer')}
        </Button>
      </div>
    );
  } else if (isLoading || !list) {
    content = (
      <InvoiceTableFrame
        footer={
          <div
            role="status"
            className="flex items-center min-h-[36px] text-sm text-gray-500"
          >
            {text('loading', 'Chargement des factures…')}
          </div>
        }
      >
        <InvoiceListSkeleton headers={headers} />
      </InvoiceTableFrame>
    );
  } else if (list.total === 0 && isFiltered) {
    content = (
      <CompactEmptyState
        title={text(
          'filteredEmptyTitle',
          'Aucune facture ne correspond à ces filtres.',
        )}
        description={
          isSearching &&
          text('searchingLine', 'Recherche de nouvelles factures dans Bestt…')
        }
        action={{
          label: text('clearFilters', 'Effacer les filtres'),
          onClick: onClearFilters,
        }}
      />
    );
  } else if (list.total === 0 && isSearching) {
    content = (
      <CompactEmptyState
        title={text('searchingTitle', 'Recherche des factures dans Bestt…')}
        description={text(
          'searchingText',
          'La liste se met à jour d’elle-même.',
        )}
      />
    );
  } else if (list.total === 0) {
    const lastPeriod = list.lastPeriodWithInvoices;
    content = (
      <CompactEmptyState
        title={fillText(text('emptyTitle', 'Aucune facture pour {month}'), {
          month: monthLabel(period),
        })}
        description={text(
          'emptyText',
          'Les factures du mois apparaîtront ici dès qu’elles seront générées. Changez de mois ou modifiez vos filtres pour en voir d’autres.',
        )}
        action={
          lastPeriod && onSelectMonth
            ? {
                label: fillText(text('seeMonth', 'Voir {month}'), {
                  month: monthLabel(lastPeriod),
                }),
                onClick: () => onSelectMonth(lastPeriod),
              }
            : undefined
        }
      />
    );
  } else {
    const isGrouped = Array.isArray(list.groups);
    // Pagination words its own count in French only: this one is from the locales.
    let countText =
      list.pages > 1
        ? fillText(
            text(
              'showingRange',
              'Affichage de {start}-{end} sur {total} résultats',
            ),
            {
              start: PAGE_SIZE * (page - 1) + 1,
              end: Math.min(PAGE_SIZE * page, list.total),
              total: list.total,
            },
          )
        : fillText(text('showingAll', 'Affichage de {total} résultats'), {
            total: list.total,
          });
    if (isGrouped) {
      countText = groupedFooterText(
        (key, fallback) => text(`grouping.${key}`, fallback),
        groupBy,
        list.total,
        list.invoiceCount ?? 0,
      );
    }
    content = (
      <InvoiceTableFrame
        above={
          isSearching && (
            <div
              role="status"
              className="flex-shrink-0 px-2 pb-2 text-sm text-gray-500"
            >
              {text(
                'searchingLine',
                'Recherche de nouvelles factures dans Bestt…',
              )}
            </div>
          )
        }
        footer={
          <div className="flex-1">
            <Pagination
              currentPage={page}
              countPerPage={PAGE_SIZE}
              countTotal={list.total}
              totalPages={list.pages}
              setCurrentPage={setPage}
              customText={countText}
              aside={
                <div className="border rounded-md py-2 px-3 text-sm font-medium bg-gray-50 whitespace-nowrap">
                  {fillText(text('total', 'Total : {amount}'), {
                    amount: formatEuros(list.totalAmountInclTax ?? 0),
                  })}
                </div>
              }
            />
          </div>
        }
      >
        <table className="table-auto w-full text-gray-500 ft-sm">
          <InvoiceTableHead
            headers={headers}
            pageCheckbox={
              !isGrouped && (
                <PageCheckbox
                  pageState={selection.pageState}
                  onToggle={selection.togglePage}
                  label={text(
                    'selection.selectPage',
                    'Sélectionner les factures de la page',
                  )}
                />
              )
            }
          />
          <tbody>
            {isGrouped
              ? list.groups.map((group) => (
                  <InvoiceGroupRow
                    key={`${group.type}:${group.id}`}
                    group={group}
                    groupBy={groupBy}
                    listQuery={listQuery}
                    refreshedWith={list}
                    selection={selection}
                  />
                ))
              : (list.items ?? []).map((invoice) => (
                  <InvoiceRow
                    key={invoice.number}
                    invoice={invoice}
                    isSelected={selection.isSelected(invoice.number)}
                    onToggle={selection.toggle}
                  />
                ))}
          </tbody>
        </table>
      </InvoiceTableFrame>
    );
  }

  return <div className="flex flex-col gap-3 flex-1">{content}</div>;
};

export default InvoiceMonthList;
